import React from 'react';
import { ChartPie } from 'lucide-react';
import { formatCurrencyAmount, numberWithCommas, removeUnderscoresAndCapitalize } from '../../../../../utils/miscFunctions';

interface PolicyDistributionWidgetProps {
  labels: string[];
  policyCount: number[];
  premium: number[];
  loading?: boolean;
}

// Distinct, repeating palette for distribution segments.
const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

/**
 * Policy Distribution — share of active policies by protection type, with volume and premium.
 * Pure presentational widget fed by the insurer protection-type-distribution data.
 */
const PolicyDistributionWidget: React.FC<PolicyDistributionWidgetProps> = ({ labels, policyCount, premium, loading }) => {
  const total = (policyCount ?? []).reduce((sum, c) => sum + (c ?? 0), 0);

  const rows = (labels ?? []).map((label, i) => ({
    label,
    count: policyCount?.[i] ?? 0,
    premium: premium?.[i] ?? 0,
    pct: total > 0 ? ((policyCount?.[i] ?? 0) / total) * 100 : 0,
    color: COLORS[i % COLORS.length],
  })).sort((a, b) => b.count - a.count);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 dark:bg-darkBg dark:border-darkBg-border">
      <div className="flex items-center gap-2 mb-4">
        <ChartPie className="h-5 w-5 text-primary-main" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Policy Distribution</h3>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-100 dark:bg-darkBg-border rounded animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No active policies yet.</p>
      ) : (
        <>
          {/* Stacked share bar */}
          <div className="flex w-full h-3 rounded-full overflow-hidden mb-5">
            {rows.map((row) => (
              <div key={row.label} style={{ width: `${row.pct}%`, backgroundColor: row.color }} title={`${row.label}: ${row.pct.toFixed(1)}%`} />
            ))}
          </div>

          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-3 w-3 rounded-sm flex-shrink-0" style={{ backgroundColor: row.color }} />
                  <span className="text-gray-700 dark:text-darkBg-mainText truncate">{removeUnderscoresAndCapitalize(row.label)}</span>
                </div>
                <div className="text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                  {numberWithCommas(row.count)} ({row.pct.toFixed(0)}%) · {formatCurrencyAmount(row.premium)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PolicyDistributionWidget;
